// --- Retail products module (removable) ---
// Retail Products — a wholesale UPC book, by supplier, with each item's
// picture looked up from the public barcode databases (Open Food Facts,
// UPCitemdb, Go-UPC, Barcode Lookup — whichever are configured) and cached in
// our own storage. Backed by api/src/functions/retailProducts.js.
//
// Two tabs: Products (the grid — filter by SUPPLIER, image status and search,
// click a card for the full record) and Imports (the upload log). Reads are
// open to any authenticated user; the import, edits and image lookups are
// Admin, server-enforced.
//
// SUPPLIER IS THE AXIS ON SCREEN; THE VENDOR IS SEARCH-ONLY. The supplier is
// the distributor whose book was imported (named on the upload), the vendor is
// the book's MFG # — the manufacturer on the row. The vendor used to be the
// filter, a tab and a line on every card; it is deliberately none of those now,
// but the search box still reaches it by name or code (the API's `q` matches
// the vendor), so a buyer who knows the manufacturer can still find its items.
//
// THE IMPORT REQUEST'S RESPONSE IS NOT THE OUTCOME — THE IMPORT ROW IS. A real
// book is ~8,000 rows and the process call can outlive the ~45 s edge, which
// answers with a bodyless 500 while the server carries on. So the modal fires
// the request and, if it fails, polls the import row until it reads Completed
// or Error (the SageImport rule). The request's own error is surfaced only
// when the row never left Pending.
import { useState, useEffect, useCallback, useRef } from 'react';
import { B, radius } from '../constants/brand';
import { fmt } from '../utils/format';
import { api } from '../api/client';
import { useApi } from '../hooks/useApi';
import {
  Btn, Input, Select, Modal, ModalFooter, Spinner, ErrorMsg, Card, Table, useToast,
  Badge, InfoNote, PageHeader, EmptyState, Icon, Tabs, KpiCard, clickable,
} from '../components/ui';

const PAGE = 60;
const LONG_TIMEOUT = 10 * 60 * 1000;
// The shared `fmt` has no plain thousands formatter; counts here use this one.
const num = (v) => Number(v || 0).toLocaleString();

// Status → what the reader sees. Kept in one place so the chip, the filter and
// the empty state agree. `placeholder` is not a bad code — it is the
// distributor's "no UPC on file" (99999999999) — and says so.
export const IMAGE_STATUS_LABELS = {
  pending:     { label: 'Awaiting lookup',  color: B.amber },
  ready:       { label: 'Image',            color: B.green },
  'not-found': { label: 'No image found',   color: B.grey },
  error:       { label: 'Lookup failed',    color: B.red },
  invalid:     { label: 'Bad UPC',          color: B.red },
  placeholder: { label: 'No UPC on file',   color: B.textLt },
  'no-upc':    { label: 'No UPC',           color: B.textLt },
  'no-source': { label: 'No image source',  color: B.textLt },
};
const statusInfo = (s) => IMAGE_STATUS_LABELS[s] || { label: s || '—', color: B.textMd };

const TAB_IDS = ['products', 'imports'];
// The Supplier filter's "items with none" choice. A sentinel the client maps to
// `noSupplier=1` — a supplier literally named "none" must stay selectable.
const NO_SUPPLIER = '__none__';
const STATUS_OPTIONS = [{ value: '', label: 'Every image status' },
  ...Object.entries(IMAGE_STATUS_LABELS).map(([value, v]) => ({ value, label: v.label }))];

// A file's contentType for the upload SAS; the API refuses anything but an image.
const imageContentType = (file) => (file?.type && /^image\//i.test(file.type)) ? file.type : null;

function useDebounced(value, ms = 300) {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

export const RetailProducts = ({ user }) => {
  const { toast } = useToast();
  const isAdmin = user?.role === 'Admin';
  const [tab, setTab] = useState('products');
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = () => setRefreshKey(k => k + 1);

  const { data: summary, loading: sumLoading, error: sumError, reload: reloadSummary } = useApi(() => api.get('retail/summary'), [refreshKey]);
  const tabLabels = {
    products: `Products (${num(summary?.total || 0)})`,
    imports: 'Imports',
  };
  const { data: supplierList } = useApi(() => api.get('retail/suppliers').catch(() => ({ available: false, items: [] })), [refreshKey]);
  const supportsSupplier = supplierList?.available !== false && summary?.supportsSupplier !== false;
  const suppliers = supplierList?.items || [];

  const [supplier, setSupplier]   = useState('');
  const [status, setStatus]       = useState('');
  const [q, setQ]                 = useState('');
  const dq = useDebounced(q);
  const [items, setItems]         = useState([]);
  const [total, setTotal]         = useState(0);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState(null);
  const [selected, setSelected]   = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const reqRef = useRef(0);

  const loadProducts = useCallback(async (offset) => {
    const myReq = ++reqRef.current;
    setListLoading(true); setListError(null);
    try {
      const params = new URLSearchParams({ limit: String(PAGE), offset: String(offset) });
      if (supplier === NO_SUPPLIER) params.set('noSupplier', '1');
      else if (supplier) params.set('supplier', supplier);
      if (status)   params.set('imageStatus', status);
      if (dq.trim()) params.set('q', dq.trim());
      const res = await api.get(`retail/products?${params}`);
      if (myReq !== reqRef.current) return;
      setTotal(res.total || 0);
      setItems(prev => (offset === 0 ? res.items : [...prev, ...res.items]));
    } catch (e) {
      if (myReq === reqRef.current) setListError(e.message);
    } finally {
      if (myReq === reqRef.current) setListLoading(false);
    }
  }, [supplier, status, dq]);

  useEffect(() => { if (summary?.available !== false) loadProducts(0); }, [loadProducts, refreshKey, summary?.available]);

  const runLookupNow = async () => {
    setLookingUp(true);
    try {
      const res = await api.post('retail/images/lookup', { limit: 40, supplier: supplier && supplier !== NO_SUPPLIER ? supplier : undefined }, { timeout: LONG_TIMEOUT });
      if (res.reason) toast.error(res.reason);
      else {
        const parts = Object.entries(res.byStatus || {}).map(([k, n]) => `${n} ${statusInfo(k).label.toLowerCase()}`);
        toast.success(res.looked ? `Looked up ${res.looked} item(s)${parts.length ? ` — ${parts.join(', ')}` : ''}` : 'Nothing is awaiting lookup.');
      }
      refresh();
    } catch (e) { toast.error(e.message); }
    finally { setLookingUp(false); }
  };

  if (sumLoading && !summary) return <Spinner />;
  if (sumError) return <ErrorMsg msg={sumError} onRetry={reloadSummary} />;
  if (summary && summary.available === false) {
    return (
      <div>
        <PageHeader icon="barcode" title="Retail Products" />
        <InfoNote>Retail products are not set up on this database yet — migration <code>{summary.migration}</code> has not run.</InfoNote>
      </div>
    );
  }

  const by = summary?.byStatus || {};
  const enabledSources = (summary?.sources || []).filter(s => s.enabled);
  const named = suppliers.filter(x => x.supplier);
  const unnamed = suppliers.find(x => !x.supplier);
  const supplierOptions = [{ value: '', label: 'Every supplier' },
    ...named.map(x => ({ value: x.supplier, label: `${x.supplier} · ${num(x.productCount)}` })),
    ...(unnamed ? [{ value: NO_SUPPLIER, label: `No supplier recorded · ${num(unnamed.productCount)}` }] : [])];

  return (
    <div>
      <PageHeader
        icon="barcode"
        title="Retail Products"
        subtitle="Each item's UPC, by supplier, with its product image looked up from the public barcode databases."
        actions={isAdmin ? (
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn variant="secondary" onClick={runLookupNow} disabled={lookingUp || !enabledSources.length} title={enabledSources.length ? 'Look up images for the next 40 items awaiting lookup' : 'No image source is enabled'}>
              <Icon name="sync" size={14} /> {lookingUp ? 'Looking up…' : 'Look up images now'}
            </Btn>
            <Btn onClick={() => setShowImport(true)}><Icon name="upload" size={14} /> Import spreadsheet</Btn>
          </div>
        ) : null}
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))', gap: 12, marginBottom: 16 }}>
        <KpiCard label="Items" value={num(summary?.total || 0)} sub={supportsSupplier ? `${num(summary?.suppliers || 0)} supplier${summary?.suppliers === 1 ? '' : 's'}` : undefined} />
        <KpiCard label="With image" value={num(by.ready || 0)} color={B.green} />
        <KpiCard label="Awaiting lookup" value={num(by.pending || 0)} color={B.amber}
          sub={summary?.sweepEnabled ? `Sweep every ${summary.sweep?.intervalMinutes} min · ${summary.sweep?.perTick} per run` : 'Sweep paused in Settings'} />
        <KpiCard label="No image found" value={num((by['not-found'] || 0) + (by.error || 0))} color={B.grey}
          sub={by.error ? `${num(by.error)} lookup failed` : undefined} />
        <KpiCard label="No usable UPC" value={num((by.placeholder || 0) + (by.invalid || 0) + (by['no-upc'] || 0))} color={B.textLt}
          sub={`${num(by.placeholder || 0)} placeholder · ${num(by.invalid || 0)} bad check digit`} />
      </div>

      <SourcesLine sources={summary?.sources || []} storageConfigured={summary?.storageConfigured} />

      {/* The shared Tabs control takes plain STRINGS and renders each one as a
          child — an { id, label } object here is React error #31 and takes the
          whole page down. The tab state stays keyed on the stable id, so the
          count in a label can move without the selection moving with it. */}
      <Tabs
        tabs={TAB_IDS.map(id => tabLabels[id])}
        active={tabLabels[tab]}
        onChange={(label) => setTab(TAB_IDS.find(id => tabLabels[id] === label) || 'products')}
      />

      {tab === 'products' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px,1.4fr) minmax(180px,1fr) minmax(200px,1.2fr)', gap: 12, margin: '14px 0' }}>
            {supportsSupplier
              ? <Select label="Supplier" value={supplier} onChange={setSupplier} options={supplierOptions} />
              : <div style={{ fontSize: 12, color: B.textMd, alignSelf: 'end' }}>Suppliers need migration <code>{supplierList?.migration || 'sql/207'}</code>.</div>}
            <Select label="Image" value={status} onChange={setStatus} options={STATUS_OPTIONS} />
            <Input label="Search" value={q} onChange={setQ} placeholder="Description, brand, item #, UPC, supplier or vendor (MFG #)" />
          </div>
          {listError && <ErrorMsg msg={listError} onRetry={() => loadProducts(0)} />}
          {!listLoading && !listError && items.length === 0 && (
            <EmptyState icon="barcode" title={summary?.total ? 'No items match' : 'No items yet'}
              message={summary?.total ? 'Try a different supplier, status or search.' : isAdmin ? 'Import the wholesale book (.xlsx) to load the UPCs.' : 'An admin imports the wholesale book to load the UPCs.'} />
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(210px,1fr))', gap: 14 }}>
            {items.map(p => <ProductCard key={p.productID} p={p} onOpen={() => setSelected(p)} />)}
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, padding: 18, color: B.textMd, fontSize: 13 }}>
            {listLoading ? <Spinner label="Loading items…" /> : (
              <>
                <span>Showing {num(items.length)} of {num(total)}</span>
                {items.length < total && <Btn variant="secondary" size="sm" onClick={() => loadProducts(items.length)}>Load more</Btn>}
              </>
            )}
          </div>
        </>
      )}

      {tab === 'imports' && <ImportsTab refreshKey={refreshKey} />}

      {selected && (
        <ProductModal
          product={selected} isAdmin={isAdmin} supportsSupplier={supportsSupplier} suppliers={named.map(x => x.supplier)} sources={enabledSources} storageConfigured={summary?.storageConfigured}
          onClose={() => setSelected(null)}
          onChanged={(p) => { setSelected(p); setItems(prev => prev.map(x => (x.productID === p.productID ? p : x))); reloadSummary(); }}
          onDeleted={(id) => { setSelected(null); setItems(prev => prev.filter(x => x.productID !== id)); setTotal(t => Math.max(0, t - 1)); reloadSummary(); }}
        />
      )}
      {showImport && <ImportModal supportsSupplier={supportsSupplier} suppliers={named.map(x => x.supplier)} onClose={() => setShowImport(false)} onDone={() => { setShowImport(false); refresh(); }} />}
    </div>
  );
};

// ── Sources line ─────────────────────────────────────────────────────────────
function SourcesLine({ sources, storageConfigured }) {
  if (!sources.length) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, fontSize: 12, color: B.textMd, marginBottom: 6 }}>
      <span style={{ fontWeight: 600 }}>Image sources, in the order tried:</span>
      {sources.map(s => (
        <span key={s.key} title={s.reason}
          style={{ padding: '3px 9px', borderRadius: radius.pill, border: `1px solid ${s.enabled ? B.green : B.border}`,
                   color: s.enabled ? B.green : B.textLt, background: s.enabled ? '#eef8f3' : B.surfaceMuted, fontWeight: 600 }}>
          {s.enabled ? '● ' : '○ '}{s.label}{s.enabled && s.tier !== 'paid' ? ` · ${s.tier}` : ''}
        </span>
      ))}
      {storageConfigured === false && <span style={{ color: B.amber }}>Images are shown from the source URL — no storage account is configured to cache them.</span>}
    </div>
  );
}

// ── Supplier chips ────────────────────────────────────────────────────────────
// The suppliers already in the book, one tap each — the shared Input forwards
// no `list` attribute, so a datalist would be inert; this is the visible form.
function SupplierChips({ names, value, onPick, disabled }) {
  if (!names?.length) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: -8, marginBottom: 8 }}>
      {names.slice(0, 8).map(name => (
        <button key={name} type="button" onClick={() => onPick(name)} disabled={disabled}
          style={{ fontSize: 12, padding: '3px 9px', borderRadius: radius.pill, border: `1px solid ${value === name ? B.navy : B.border}`,
                   background: value === name ? B.navy : B.surface, color: value === name ? '#fff' : B.textMd, cursor: disabled ? 'default' : 'pointer' }}>
          {name}
        </button>
      ))}
    </div>
  );
}

// ── Product card ──────────────────────────────────────────────────────────────
function Thumb({ p, size = 150 }) {
  const [broken, setBroken] = useState(false);
  const url = !broken && p.imageStatus === 'ready' ? p.imageUrl : null;
  return (
    <div style={{ height: size, background: B.surfaceMuted, borderRadius: radius.lg, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
      {url ? <img src={url} alt={p.description || p.upc || ''} onError={() => setBroken(true)} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
           : <div style={{ textAlign: 'center', color: B.textLt, fontSize: 12 }}><Icon name="image" size={28} /><div>{statusInfo(p.imageStatus).label}</div></div>}
    </div>
  );
}

function ProductCard({ p, onOpen }) {
  const st = statusInfo(p.imageStatus);
  return (
    <Card interactive {...clickable(onOpen)} style={{ padding: 12, cursor: 'pointer' }}>
      <Thumb p={p} />
      <div style={{ marginTop: 10, fontWeight: 600, fontSize: 13, color: B.text, lineHeight: 1.3, minHeight: 34 }}>{p.description || p.lookupTitle || '(no description)'}</div>
      <div style={{ fontSize: 12, color: B.textMd, marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {p.brand || '—'}{p.supplier ? ` · ${p.supplier}` : ''}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
        <span style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11, color: B.textMd }}>{p.upc || p.upcRaw || 'no UPC'}</span>
        <Badge color={st.color}>{st.label}</Badge>
      </div>
    </Card>
  );
}

// ── Product modal ─────────────────────────────────────────────────────────────
function ProductModal({ product, isAdmin, supportsSupplier, suppliers, sources, storageConfigured, onClose, onChanged, onDeleted }) {
  const { toast } = useToast();
  const [p, setP] = useState(product);
  const [busy, setBusy] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [supplierDraft, setSupplierDraft] = useState(product.supplier || '');
  const [err, setErr] = useState(null);
  useEffect(() => { setP(product); setSupplierDraft(product.supplier || ''); }, [product]);
  const st = statusInfo(p.imageStatus);

  const run = async (label, fn) => {
    setBusy(label); setErr(null);
    try { const next = await fn(); if (next) { setP(next); onChanged(next); } }
    catch (e) { setErr(e.message); }
    finally { setBusy(null); }
  };
  const lookup = () => run('lookup', async () => {
    const res = await api.post(`retail/products/${p.productID}/lookup-image`, {}, { timeout: 120_000 });
    toast.success(res.status === 'ready' ? 'Image found' : `Lookup finished: ${statusInfo(res.status).label.toLowerCase()}`);
    return res.product;
  });
  const saveSupplier = () => run('supplier', () => api.put(`retail/products/${p.productID}`, { supplier: supplierDraft.trim() || null }));
  const supplierDirty = (supplierDraft.trim() || '') !== (p.supplier || '');
  const useUrl = () => run('url', () => api.put(`retail/products/${p.productID}`, { imageUrl: imageUrl.trim() }, { timeout: 60_000 }));
  const clearImage = () => run('clear', () => api.put(`retail/products/${p.productID}`, { clearImage: true }));
  const remove = () => {
    if (!window.confirm('Remove this item from the book? It can be re-imported.')) return;
    run('delete', async () => { await api.del(`retail/products/${p.productID}`); onDeleted(p.productID); return null; });
  };
  const upload = (file) => run('upload', async () => {
    const contentType = imageContentType(file);
    if (!contentType) throw new Error('Choose an image file (JPEG, PNG, WebP or GIF).');
    const { sasUrl, blobPath } = await api.post(`retail/products/${p.productID}/image-upload-url`, { fileName: file.name, contentType });
    const put = await fetch(sasUrl, { method: 'PUT', headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': contentType }, body: file });
    if (!put.ok) throw new Error(`Upload failed (${put.status})`);
    return api.post(`retail/products/${p.productID}/image-confirm`, { blobPath });
  });

  const rows = [
    ['Item #', p.itemNumber], ['UPC', p.upc ? `${p.upc}${p.upcKind ? ` (${p.upcKind})` : ''}` : '—'],
    ['As imported', p.upcRaw || '—'], ['Brand', p.brand], ['Supplier', p.supplier || '—'],
    ['Category / class', [p.categoryCode, p.classCode].filter(Boolean).join(' / ') || '—'],
    ['Pack · size', [p.pack, p.size].filter(Boolean).join(' · ') || '—'], ['Selling unit', p.sellingUnit || '—'],
    ['W/S cost', p.wholesaleCost != null ? fmt.currency(p.wholesaleCost) : '—'], ['Customer #', p.customerCode || '—'],
  ];

  return (
    <Modal title={p.description || p.lookupTitle || 'Item'} onClose={onClose} width={760}>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 300px) 1fr', gap: 20 }}>
        <div>
          <Thumb p={p} size={280} />
          <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <Badge color={st.color}>{st.label}</Badge>
            {p.imageSource && <span style={{ fontSize: 12, color: B.textMd }}>from {p.imageSource === 'manual' ? 'a person' : (sources.find(s => s.key === p.imageSource)?.label || p.imageSource)}</span>}
          </div>
          {p.imageLookupError && <div style={{ fontSize: 12, color: B.red, marginTop: 6 }}>{p.imageLookupError}</div>}
          {p.lookupTitle && p.lookupTitle !== p.description && <div style={{ fontSize: 12, color: B.textMd, marginTop: 6 }}>Database title: <i>{p.lookupTitle}</i>{p.lookupBrand ? ` (${p.lookupBrand})` : ''}</div>}
          {p.imageStatus === 'placeholder' && <div style={{ fontSize: 12, color: B.textMd, marginTop: 6 }}>The book carries a placeholder code for this item, so there is nothing to look up. An image can still be added by hand.</div>}
          {p.imageStatus === 'invalid' && <div style={{ fontSize: 12, color: B.textMd, marginTop: 6 }}>The code fails its check digit ({p.upcReason}); it is probably mistyped in the book.</div>}
          {p.imageSourceUrl && p.imageSource !== 'manual' && <a href={p.imageSourceUrl} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: B.accent, display: 'inline-block', marginTop: 6 }}>Source image ↗</a>}
        </div>
        <div>
          <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
            <tbody>
              {rows.map(([k, v]) => (
                <tr key={k} style={{ borderBottom: `1px solid ${B.divider}` }}>
                  <td style={{ padding: '6px 8px 6px 0', color: B.textMd, whiteSpace: 'nowrap', verticalAlign: 'top' }}>{k}</td>
                  <td style={{ padding: '6px 0', color: B.text, fontFamily: k === 'UPC' || k === 'As imported' ? 'ui-monospace, Menlo, monospace' : undefined }}>{v || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {isAdmin && (
            <div style={{ marginTop: 16, display: 'grid', gap: 12 }}>
              {supportsSupplier && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                  <div style={{ flex: 1 }}>
                    <Input label="Supplier" value={supplierDraft} onChange={setSupplierDraft} placeholder="Who this item is bought through" disabled={!!busy} />
                  </div>
                  <Btn size="sm" onClick={saveSupplier} disabled={!!busy || !supplierDirty} style={{ marginBottom: 16 }}>{busy === 'supplier' ? 'Saving…' : 'Save'}</Btn>
                </div>
              )}
              {supportsSupplier && (suppliers || []).length > 0 && supplierDirty === false && !p.supplier && (
                <SupplierChips names={suppliers} value={supplierDraft} onPick={setSupplierDraft} disabled={!!busy} />
              )}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Btn variant="secondary" size="sm" onClick={lookup} disabled={!!busy || !p.upcValid || !sources.length}
                  title={!p.upcValid ? 'No lookable UPC on this item' : !sources.length ? 'No image source is enabled' : 'Ask the barcode databases again'}>
                  <Icon name="sync" size={13} /> {busy === 'lookup' ? 'Looking up…' : 'Look up image'}
                </Btn>
                {storageConfigured !== false && (
                  <label style={{ display: 'inline-flex' }}>
                    <Btn variant="secondary" size="sm" disabled={!!busy} onClick={() => {}} style={{ pointerEvents: 'none' }}><Icon name="upload" size={13} /> {busy === 'upload' ? 'Uploading…' : 'Upload image'}</Btn>
                    <input type="file" accept="image/*" style={{ display: 'none' }} disabled={!!busy} onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) upload(f); }} />
                  </label>
                )}
                {p.imageStatus === 'ready' && <Btn variant="ghost" size="sm" onClick={clearImage} disabled={!!busy}>Remove image</Btn>}
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                <div style={{ flex: 1 }}><Input label="Or paste an image URL" value={imageUrl} onChange={setImageUrl} placeholder="https://…" disabled={!!busy} /></div>
                <Btn size="sm" onClick={useUrl} disabled={!!busy || !/^https?:\/\//i.test(imageUrl.trim())}>{busy === 'url' ? 'Fetching…' : 'Use it'}</Btn>
              </div>
              {err && <div style={{ color: B.red, fontSize: 12 }}>{err}</div>}
            </div>
          )}
        </div>
      </div>
      <ModalFooter>
        {isAdmin && <Btn variant="ghost" onClick={remove} disabled={!!busy} style={{ color: B.red, marginRight: 'auto' }}>Remove item</Btn>}
        <Btn variant="ghost" onClick={onClose}>Close</Btn>
      </ModalFooter>
    </Modal>
  );
}

// ── Imports tab ───────────────────────────────────────────────────────────────
function ImportsTab({ refreshKey }) {
  const { data, loading, error, reload } = useApi(() => api.get('retail/imports'), [refreshKey]);
  if (loading) return <Spinner />;
  if (error) return <ErrorMsg msg={error} onRetry={reload} />;
  if (!data?.length) return <EmptyState icon="upload" title="No imports yet" message="Import the wholesale book (.xlsx) from the button above." />;
  return (
    <Card style={{ padding: 0, marginTop: 14 }}>
      <Table
        columns={[
          { key: 'createdUTC', label: 'When', render: v => fmt.date(v) },
          { key: 'fileName', label: 'File' },
          { key: 'supplier', label: 'Supplier', render: v => v || <span style={{ color: B.textLt }}>—</span> },
          { key: 'status', label: 'Status', render: v => <Badge color={v === 'Completed' ? B.green : v === 'Error' ? B.red : B.amber}>{v}</Badge> },
          { key: 'rowsTotal', label: 'Rows', render: v => (v == null ? '—' : num(v)) },
          { key: 'rowsInserted', label: 'Added', render: v => (v == null ? '—' : num(v)) },
          { key: 'rowsUpdated', label: 'Updated', render: v => (v == null ? '—' : num(v)) },
          { key: 'rowsSkipped', label: 'Skipped', render: v => (v == null ? '—' : num(v)) },
          { key: 'errorMessage', label: 'Notes', render: v => <span style={{ fontSize: 12, color: B.textMd }}>{v || ''}</span> },
        ]}
        data={data}
      />
    </Card>
  );
}

// ── Import modal ──────────────────────────────────────────────────────────────
const POLL_MS = 3000;
const POLL_MAX_MS = 10 * 60 * 1000;

export function ImportModal({ supportsSupplier = true, suppliers = [], onClose, onDone }) {
  const { toast } = useToast();
  const [file, setFile] = useState(null);
  const [supplier, setSupplier] = useState('');
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState('');
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);

  // The import row is the outcome (see the header). Poll it until it settles.
  const waitForRow = async (importId) => {
    const started = Date.now();
    while (Date.now() - started < POLL_MAX_MS) {
      await new Promise(r => setTimeout(r, POLL_MS));
      const rows = await api.get('retail/imports').catch(() => null);
      const row = rows?.find(r => String(r.importID).toLowerCase() === String(importId).toLowerCase());
      if (!row) continue;
      if (row.status === 'Completed') return { rowsTotal: row.rowsTotal, inserted: row.rowsInserted, updated: row.rowsUpdated, skipped: row.rowsSkipped, supplier: row.supplier, note: row.errorMessage };
      if (row.status === 'Error') throw new Error(row.errorMessage || 'Import failed');
      if (row.status === 'Pending') throw Object.assign(new Error('The import was never picked up.'), { neverStarted: true });
      setPhase('Import is running on the server…');
    }
    throw new Error('The import is still running; check the Imports tab in a minute.');
  };

  const run = async () => {
    if (!file) { setErr('Choose a spreadsheet to import'); return; }
    if (supportsSupplier && !supplier.trim()) { setErr('Name the supplier this book came from'); return; }
    setBusy(true); setErr(null); setResult(null);
    let importId = null;
    try {
      setPhase('Uploading…');
      const up = await api.post('retail/import', { fileName: file.name, supplier: supportsSupplier ? supplier.trim() : undefined });
      importId = up.importId;
      const put = await fetch(up.sasUrl, { method: 'PUT', headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': up.contentType || file.type || 'application/octet-stream' }, body: file });
      if (!put.ok) throw new Error(`Upload failed (${put.status})`);
      setPhase('Reading the book…');
      let res;
      try {
        res = await api.post(`retail/import/${importId}/process`, {}, { timeout: LONG_TIMEOUT });
      } catch (e) {
        // A 4xx is the server's refusal, made before any work; a 5xx or a cut
        // connection routinely means the edge gave up on a run that is still
        // going, so the row decides.
        if (e.status && e.status < 500) throw e;
        setPhase('The request timed out; watching the import row…');
        try { res = await waitForRow(importId); }
        catch (pollErr) { throw pollErr.neverStarted ? e : pollErr; }
      }
      setResult(res);
      toast.success(`Imported ${num((res.inserted || 0) + (res.updated || 0))} item(s)`);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false); setPhase('');
    }
  };

  return (
    <Modal title="Import a wholesale book" onClose={onClose} width={560}>
      <div style={{ fontSize: 13, color: B.textMd, lineHeight: 1.5, marginBottom: 14 }}>
        Upload the distributor's item list (<b>.xlsx</b>, .xls or .csv). The sheet needs a <b>UPC</b> column and an <b>item number</b>;
        brand, description, pack, size and W/S cost are kept where present, and <b>MFG #</b> is kept as the manufacturer (reachable through the search).
        Every item lands under the <b>supplier</b> named below — a <i>Supplier</i> column in the sheet wins per row.
        Re-importing a book updates the items already here (matched by item number) and adds the rest. Images are then looked
        up in the background — the sweep runs every ten minutes — or on demand with <i>Look up images now</i>.
      </div>
      {supportsSupplier && (
        <div style={{ marginBottom: 12 }}>
          <Input label="Supplier" value={supplier} onChange={setSupplier} placeholder="The distributor this book came from" required disabled={busy} />
          <SupplierChips names={suppliers} value={supplier} onPick={setSupplier} disabled={busy} />
        </div>
      )}
      <input type="file" accept=".xlsx,.xls,.csv" onChange={e => { setFile(e.target.files?.[0] || null); setResult(null); setErr(null); }} style={{ fontSize: 13 }} />
      {phase && <div style={{ fontSize: 12, color: B.textMd, marginTop: 10 }}>{phase}</div>}
      {err && <div style={{ color: B.red, fontSize: 12, marginTop: 10 }}>{err}</div>}
      {result && (
        <div style={{ marginTop: 14, fontSize: 13, color: B.text, background: B.surfaceMuted, borderRadius: 6, padding: '10px 12px', lineHeight: 1.6 }}>
          <b>{num(result.inserted || 0)}</b> item(s) added, <b>{num(result.updated || 0)}</b> updated
          {result.skipped ? <>, {num(result.skipped)} skipped</> : null}
          {result.supplier ? <> under <b>{result.supplier}</b></> : null}.
          {result.counts && <div style={{ fontSize: 12, color: B.textMd }}>{num(result.counts.valid)} lookable UPCs · {num(result.counts.placeholder)} placeholder codes · {num(result.counts.invalid)} failed the check digit</div>}
          {result.unmapped?.length ? <div style={{ fontSize: 12, color: B.textMd }}>Columns not imported: {result.unmapped.join(', ')}</div> : null}
          {result.note && !result.counts ? <div style={{ fontSize: 12, color: B.textMd }}>{result.note}</div> : null}
        </div>
      )}
      <ModalFooter>
        <Btn variant="ghost" onClick={onClose} disabled={busy}>{result ? 'Close' : 'Cancel'}</Btn>
        {!result && <Btn onClick={run} disabled={busy || !file || (supportsSupplier && !supplier.trim())}>{busy ? 'Importing…' : 'Upload & import'}</Btn>}
        {result && <Btn onClick={onDone}>Done</Btn>}
      </ModalFooter>
    </Modal>
  );
}
